/**
 * Firebase Sync Service
 * 
 * Handles synchronization of workout logs, recovery metrics, and 1RMs to Firestore.
 * Implements offline-first approach with automatic sync when online.
 */

import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp,
  addDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from './firebase';
import type { SessionLog } from './sessionStorage';
import type { RecoveryMetrics } from './types/recovery';
import { storage } from './storage';

// OneRMData type definition
export interface OneRMData {
  [exerciseName: string]: number;
}

/**
 * Remove undefined values from object (Firestore doesn't allow undefined)
 * Recursively cleans nested objects
 */
function removeUndefined<T extends Record<string, any>>(obj: T): T {
  const cleaned: any = {};
  
  for (const key in obj) {
    const value = obj[key];
    
    if (value === undefined) {
      // Skip undefined values
      continue;
    } else if (value === null) {
      // Keep null values
      cleaned[key] = null;
    } else if (Array.isArray(value)) {
      // Clean arrays
      cleaned[key] = value.map((item: any) => 
        typeof item === 'object' && item !== null ? removeUndefined(item) : item
      );
    } else if (typeof value === 'object' && value !== null) {
      // Check if it's a special object type (Date, Timestamp, etc.)
      const isSpecialType = (value as any) instanceof Date || 
                           (value as any).constructor?.name === 'Timestamp' ||
                           ((value as any).seconds !== undefined && (value as any).nanoseconds !== undefined);
      
      if (isSpecialType) {
        // Keep as is
        cleaned[key] = value;
      } else {
        // Recursively clean nested objects
        cleaned[key] = removeUndefined(value);
      }
    } else {
      // Keep all other values
      cleaned[key] = value;
    }
  }
  
  return cleaned as T;
}

// ==================== SESSION LOGS ====================

/**
 * Save session log to Firestore
 */
export async function saveSessionToFirebase(session: SessionLog): Promise<void> {
  try {
    const sessionRef = doc(db, 'users', session.userId, 'sessions', session.id);
    
    // Convert dates to Firestore Timestamps
    //  FIX: Add updatedAt for incremental sync to detect changes
    const sessionData = {
      ...session,
      date: Timestamp.fromDate(session.date),
      startTime: Timestamp.fromDate(session.startTime),
      endTime: session.endTime ? Timestamp.fromDate(session.endTime) : null,
      syncedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    
    // Remove undefined values (Firestore doesn't allow them)
    const cleanedData = removeUndefined(sessionData);
    
    await setDoc(sessionRef, cleanedData);
    console.log(' Session saved to Firebase:', session.id);
  } catch (error) {
    console.error(' Error saving session to Firebase:', error);
    throw error;
  }
}

/**
 * Get user's session history from Firestore
 */
export async function getSessionHistoryFromFirebase(
  userId: string, 
  limitCount: number = 30
): Promise<SessionLog[]> {
  try {
    const sessionsRef = collection(db, 'users', userId, 'sessions');
    const q = query(
      sessionsRef, 
      orderBy('date', 'desc'), 
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    
    const sessions: SessionLog[] = snapshot.docs.map(doc => {
      const data = doc.data();
      
      // Helper to safely convert Firestore Timestamp to Date
      const toDateSafe = (val: any): Date => {
        if (!val) return new Date();
        if (val.toDate && typeof val.toDate === 'function') return val.toDate(); // Firestore Timestamp
        if (val instanceof Date) return val; // Already a Date
        if (typeof val === 'string' || typeof val === 'number') return new Date(val); // String or timestamp
        return new Date();
      };
      
      return {
        ...data,
        date: toDateSafe(data.date),
        startTime: toDateSafe(data.startTime),
        endTime: toDateSafe(data.endTime),
      } as SessionLog;
    });
    
    console.log(` Loaded ${sessions.length} sessions from Firebase`);
    return sessions;
  } catch (error) {
    console.error(' Error loading sessions from Firebase:', error);
    return [];
  }
}

/**
 * Get specific session by ID
 */
export async function getSessionFromFirebase(
  userId: string, 
  sessionId: string
): Promise<SessionLog | null> {
  try {
    const sessionRef = doc(db, 'users', userId, 'sessions', sessionId);
    const snapshot = await getDoc(sessionRef);
    
    if (!snapshot.exists()) {
      return null;
    }
    
    const data = snapshot.data();
    
    // Helper to safely convert Firestore Timestamp to Date
    const toDateSafe = (val: any): Date => {
      if (!val) return new Date();
      if (val.toDate && typeof val.toDate === 'function') return val.toDate();
      if (val instanceof Date) return val;
      if (typeof val === 'string' || typeof val === 'number') return new Date(val);
      return new Date();
    };
    
    return {
      ...data,
      date: toDateSafe(data.date),
      startTime: toDateSafe(data.startTime),
      endTime: toDateSafe(data.endTime),
    } as SessionLog;
  } catch (error) {
    console.error(' Error loading session from Firebase:', error);
    return null;
  }
}

// ==================== RECOVERY METRICS ====================

/**
 * Save recovery metrics to Firestore
 */
export async function saveRecoveryToFirebase(metrics: RecoveryMetrics): Promise<void> {
  try {
    const recoveryRef = doc(db, 'users', metrics.userId, 'recovery', metrics.id);
    
    // Convert date to Firestore Timestamp
    //  FIX: Add updatedAt for incremental sync to detect changes
    const recoveryData = {
      ...metrics,
      date: Timestamp.fromDate(metrics.date),
      syncedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    
    // Remove undefined values (Firestore doesn't allow them)
    const cleanedData = removeUndefined(recoveryData);
    
    await setDoc(recoveryRef, cleanedData);
    console.log(' Recovery metrics saved to Firebase:', metrics.id);
  } catch (error) {
    console.error(' Error saving recovery metrics to Firebase:', error);
    throw error;
  }
}

/**
 * Get user's recovery history from Firestore
 */
export async function getRecoveryHistoryFromFirebase(
  userId: string, 
  limitCount: number = 30
): Promise<RecoveryMetrics[]> {
  try {
    const recoveryRef = collection(db, 'users', userId, 'recovery');
    const q = query(
      recoveryRef, 
      orderBy('date', 'desc'), 
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    
    const recovery: RecoveryMetrics[] = snapshot.docs.map(doc => {
      const data = doc.data();
      
      // Helper to safely convert Firestore Timestamp to Date
      const toDateSafe = (val: any): Date => {
        if (!val) return new Date();
        if (val.toDate && typeof val.toDate === 'function') return val.toDate();
        if (val instanceof Date) return val;
        if (typeof val === 'string' || typeof val === 'number') return new Date(val);
        return new Date();
      };
      
      return {
        ...data,
        date: toDateSafe(data.date),
      } as RecoveryMetrics;
    });
    
    console.log(` Loaded ${recovery.length} recovery entries from Firebase`);
    return recovery;
  } catch (error) {
    console.error(' Error loading recovery metrics from Firebase:', error);
    return [];
  }
}

/**
 * Get today's recovery metrics from Firestore
 */
export async function getTodayRecoveryFromFirebase(userId: string): Promise<RecoveryMetrics | null> {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const recoveryRef = collection(db, 'users', userId, 'recovery');
    const q = query(
      recoveryRef,
      where('date', '>=', Timestamp.fromDate(today)),
      where('date', '<', Timestamp.fromDate(tomorrow)),
      limit(1)
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    const data = snapshot.docs[0].data();
    
    // Helper to safely convert Firestore Timestamp to Date
    const toDateSafe = (val: any): Date => {
      if (!val) return new Date();
      if (val.toDate && typeof val.toDate === 'function') return val.toDate();
      if (val instanceof Date) return val;
      if (typeof val === 'string' || typeof val === 'number') return new Date(val);
      return new Date();
    };
    
    return {
      ...data,
      date: toDateSafe(data.date),
    } as RecoveryMetrics;
  } catch (error) {
    console.error(' Error loading today\'s recovery from Firebase:', error);
    return null;
  }
}

// ==================== 1RM DATA ====================

/**
 * Save 1RM data to Firestore
 */
export async function save1RMToFirebase(
  userId: string, 
  oneRMs: Record<string, number> | Record<string, OneRMData>
): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    
    await setDoc(userRef, {
      oneRMs,
      updatedAt: Timestamp.now(),
    }, { merge: true });
    
    console.log(' 1RM data saved to Firebase');
  } catch (error) {
    console.error(' Error saving 1RM data to Firebase:', error);
    throw error;
  }
}

/**
 * Overwrite the entire oneRMs map in Firestore (REPLACES, does not merge).
 *
 * save1RMToFirebase uses setDoc with { merge: true }, which deep-merges the
 * map and therefore CANNOT delete stale keys. De-dup migrations need keys to
 * actually disappear, so they must use updateDoc, which replaces the whole
 * field value.
 */
export async function overwrite1RMInFirebase(
  userId: string,
  oneRMs: Record<string, number>
): Promise<void> {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, { oneRMs, updatedAt: Timestamp.now() });
  console.log(' 1RM map overwritten in Firebase (de-dup)');
}

/**
 * Get 1RM data from Firestore
 */
export async function get1RMFromFirebase(userId: string): Promise<Record<string, number>> {
  try {
    const userRef = doc(db, 'users', userId);
    const snapshot = await getDoc(userRef);
    
    if (!snapshot.exists()) {
      return {};
    }
    
    const data = snapshot.data();
    return data.oneRMs || {};
  } catch (error) {
    console.error(' Error loading 1RM data from Firebase:', error);
    return {};
  }
}

// ==================== USER PROFILE ====================

/**
 * Update user profile
 */
export async function updateUserProfile(
  userId: string, 
  profileData: {
    displayName?: string;
    bodyWeight?: number;
    height?: number;
    age?: number;
    experienceLevel?: 'beginner' | 'intermediate' | 'advanced' | 'elite';
    goals?: string[];
  }
): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    
    await setDoc(userRef, {
      ...profileData,
      updatedAt: Timestamp.now(),
    }, { merge: true });
    
    console.log(' User profile updated');
  } catch (error) {
    console.error(' Error updating user profile:', error);
    throw error;
  }
}

/**
 * Get user profile
 */
export async function getUserProfile(userId: string): Promise<any> {
  try {
    const userRef = doc(db, 'users', userId);
    const snapshot = await getDoc(userRef);
    
    if (!snapshot.exists()) {
      return null;
    }
    
    return snapshot.data();
  } catch (error) {
    console.error(' Error loading user profile:', error);
    return null;
  }
}

// ==================== SYNC HELPERS ====================

/**
 * Sync all local data to Firebase
 * (For migrating from localStorage to Firebase)
 */
export async function syncAllLocalDataToFirebase(userId: string): Promise<{
  sessions: number;
  recovery: number;
  oneRMs: boolean;
}> {
  console.log('� Starting full sync to Firebase...');
  
  let sessionCount = 0;
  let recoveryCount = 0;
  let oneRMsSynced = false;
  
  try {
    // Sync sessions
    const sessionsStr = storage.get('sessionHistory');
    if (sessionsStr) {
      const sessions: SessionLog[] = JSON.parse(sessionsStr);
      for (const session of sessions) {
        await saveSessionToFirebase({ ...session, userId });
        sessionCount++;
      }
    }
    
    // Sync recovery
    const recoveryStr = storage.get('recoveryHistory');
    if (recoveryStr) {
      const recovery: RecoveryMetrics[] = JSON.parse(recoveryStr);
      for (const metrics of recovery) {
        await saveRecoveryToFirebase({ ...metrics, userId });
        recoveryCount++;
      }
    }
    
    // Sync 1RMs
    const oneRMsStr = storage.get('oneRMs');
    if (oneRMsStr) {
      const oneRMs = JSON.parse(oneRMsStr);
      await save1RMToFirebase(userId, oneRMs);
      oneRMsSynced = true;
    }
    
    console.log(` Sync complete: ${sessionCount} sessions, ${recoveryCount} recovery entries, 1RMs: ${oneRMsSynced}`);
    
    return { sessions: sessionCount, recovery: recoveryCount, oneRMs: oneRMsSynced };
  } catch (error) {
    console.error(' Error during sync:', error);
    throw error;
  }
}

/**
 * Load all data from Firebase to Dexie
 *  REFACTORED: Now uses Dexie instead of localStorage
 * (For initial app load after login)
 */
export async function loadAllDataFromFirebase(userId: string): Promise<void> {
  console.log('� Loading data from Firebase to Dexie...');

  try {
    // Use the Dexie-based full download (includes 1RMs)
    const { forceFullDownloadFromFirebase } = await import('./dexieFirebaseSync');
    const stats = await forceFullDownloadFromFirebase(userId);
    console.log(` Data loaded from Firebase: ${stats.sessions} sessions, ${stats.recovery} recovery, ${stats.oneRMs} 1RMs`);

    //  FIX: 1RMs are now stored ONLY in Dexie (downloaded by forceFullDownloadFromFirebase)
    // Keep localStorage in sync for backwards compatibility with components that still read from it
    // This is a transitional measure - components should migrate to reading from Dexie directly
    const { get1RMs } = await import('./db');
    const dexie1RMs = await get1RMs(userId);
    if (Object.keys(dexie1RMs).length > 0) {
      storage.set('oneRMs', JSON.stringify(dexie1RMs));
      console.log(' Synced 1RMs from Dexie to localStorage for backwards compatibility');
    }

    console.log(' All data loaded from Firebase');
  } catch (error) {
    console.error(' Error loading data from Firebase:', error);
    throw error;
  }
}

